import { ClientRequestApiService, ClientRequestModel, ClientRequestStatus, DeliverableModel } from '@aiwyn/api';
import {
  BaseCommonClientRequest,
  BaseCommonClientRequestGroup,
  ClientRequestGroupStatus,
  CommonClientRequestDocumentUpload,
  isSafeSendDeliverable,
  REQUEST_IDENTITY_TYPE,
  UpdateDeliverablesFromSubscription,
} from '@aiwyn/common/client-requests';
import { DATA_SELECTOR_INJECTION_TOKEN, ignoreNullish, isEmpty, LoadableStatus, PillColor } from '@aiwyn/shared';
import { Component, HostBinding, HostListener, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, defer, firstValueFrom, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { DeletedDeliverableDialogComponent, DeletedDeliverableDialogData } from '../../components';
import { ALL_DELIVERABLES, ClientRequestsLayout } from '../../models';
import { ClientRequestsLayoutService } from '../../services';
import { ClientRequestsState, SetSelectedClientRequestGroupIdentity, SetSelectedClientRequestId } from '../../state';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';

interface StatusPill {
  color: PillColor;
  text: string;
}

type CommonClientRequest = BaseCommonClientRequest<ClientRequestModel, CommonClientRequestDocumentUpload>;

interface ClientRequestSections {
  incomplete: CommonClientRequest[];
  complete: CommonClientRequest[];
}

const REQUESTS_TAB_INDEX = 0;
const DELIVERABLES_TAB_INDEX = 1;
type ClientRequestGroupTabIndex = typeof REQUESTS_TAB_INDEX | typeof DELIVERABLES_TAB_INDEX;

@Component({
  selector: 'app-client-request-group',
  templateUrl: './client-request-group.component.html',
  styleUrls: ['./client-request-group.component.scss', '../../styles/client-requests.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'client-requests-group',
    },
  ],
  standalone: false,
})
export class ClientRequestGroupComponent implements OnInit, OnDestroy {
  @Input() instance!: BaseCommonClientRequestGroup;

  private readonly _unsubscribe$ = new Subject<void>();
  @HostBinding('class.locked') isLockedHostClass = false;
  readonly isSelected$ = new BehaviorSubject<boolean>(false);

  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);
  private readonly launchDarklyService = inject(LaunchDarklyService);

  @Input() set isSelected(isSelected: boolean) {
    this.isSelected$.next(isSelected);
  }

  isFullLayout$ = this.layoutService.layout$.pipe(
    map(layout => layout === ClientRequestsLayout.FULL),
    shareReplay(1),
  );

  applyTransitionClasses$ = this.layoutService.applyTransitionClasses$.pipe(
    map(apply => apply && this.isSelected$.value),
    shareReplay(1),
  );

  applyInverseTransitionClasses$ = this.layoutService.applyInverseTransitionClasses$.pipe(
    map(apply => apply || !this.isSelected$.value),
    shareReplay(1),
  );

  name$: Observable<string> = defer(() => this.instance.typeClass$).pipe(
    switchMap(typeClass => typeClass.name$),
  );

  status$: Observable<ClientRequestGroupStatus> = defer(() => this.instance.typeClass$).pipe(
    switchMap(typeClass => typeClass.status$),
    shareReplay(1),
  );

  isGroupComplete$: Observable<boolean> = this.status$.pipe(
    map(status => status === ClientRequestGroupStatus.COMPLETED),
  );

  isLocked$: Observable<boolean> = combineLatest([
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_REQUEST_LISTS_LOCKDOWN),
    defer(() => this.instance.isLocked$).pipe(startWith(false)),
  ]).pipe(
    map(([flagEnabled, isLocked]) => flagEnabled && isLocked),
    shareReplay(1),
  );

  statusPill$: Observable<StatusPill> = this.status$.pipe(
    map<string, StatusPill>(status => ({
      color: status === ClientRequestGroupStatus.OPEN ? 'gray' : 'green',
      text: status,
    })),
    shareReplay(1),
  );

  clientNames$: Observable<string[]> = defer(() => this.instance.typeClass$).pipe(
    switchMap(typeClass => typeClass.client$.pipe(
      switchMap(client => client.data$.pipe(
        map(clientInfo => clientInfo?.name ? [clientInfo.name] : [])
      ))
    )),
    shareReplay(1)
  );
requestsCount$: Observable<number> = defer(() => this.instance.requests$).pipe(
    map(requests => requests.length),
  );
  requestSections$: Observable<ClientRequestSections> = defer(() => this.instance.requests$).pipe(
    switchMap(requests => combineLatest(
      requests.map((request, index) => combineLatest([request.status$, request.completedAt$]).pipe(
        map(([status, completedAt]) => ({ index, status, completedAt })),
      )),
    ).pipe(
      map(statuses => {
        const areAllCompleted = statuses.every(({ status }) => status === ClientRequestStatus.DONE);
        return statuses.reduce(
          (sections, { index, status }) => {
            const section: keyof ClientRequestSections = !areAllCompleted && status === ClientRequestStatus.DONE
              ? 'complete'
              : 'incomplete';

            sections[section].push(requests[index]);
            return sections;
          },
          { incomplete: [], complete: [] } as ClientRequestSections,
        );
      }),
    )),
    shareReplay(1),
  );

  sortedIncompleteRequests$: Observable<CommonClientRequest[]> = this.requestSections$.pipe(
    ignoreNullish(),
    map(({ incomplete }) => incomplete),
    switchMap(requests => this.sortRequests$(requests)),
  );

  incompleteRequestsCount$: Observable<number> = this.sortedIncompleteRequests$.pipe(
    switchMap(requests => combineLatest(requests.map(request => request.status$))),
    map(statuses => statuses.filter(status => status === ClientRequestStatus.IN_PROGRESS).length),
    shareReplay(1),
  );

  hasRejectedItems$: Observable<boolean> = defer(() => this.instance.requests$).pipe(
    switchMap(requests => {
      if (isEmpty(requests)) {
        return of(false);
      }

      return combineLatest(
        requests.map(request => request.rejectedItems$)
      ).pipe(
        map(rejectedItemsArrays =>
          rejectedItemsArrays.some(items => items && items.length > 0)
        )
      );
    }),
    shareReplay(1),
  );

  sortedCompleteRequests$: Observable<CommonClientRequest[]> = this.requestSections$.pipe(
    ignoreNullish(),
    map(({ complete }) => complete),
    switchMap(requests => this.sortRequests$(requests)),
  );

  sortRequests$(requests: CommonClientRequest[]): Observable<CommonClientRequest[]> {
    return combineLatest(
      requests.map((request, index) => combineLatest([request.nextDueDate$, request.completedAt$, request.name$]).pipe(
        map(([dueDate, completedAt, name]) => ({ index, dueDate, completedAt, name })),
      )),
    ).pipe(
      map(sortingInfo => sortingInfo.sort((a, b) => {
        const dateComparison = a.completedAt
          ? (a.completedAt).localeCompare(b.completedAt)
          : (a.dueDate || '9999').localeCompare(b.dueDate || '9999'); // if there is no due date, we want it after the ones with due dates

        // if due dates are the same, sort by name
        return dateComparison === 0 ? a.name.localeCompare(b.name) : dateComparison;
      })),
      map(sortingInfo => sortingInfo.map(({ index }) => requests[index])),
    );
  }

  deliverablesCount$: Observable<number> = defer(() => this.instance.deliverables$).pipe(
    map(deliverables => deliverables.length),
  );

  selectedTab$ = new BehaviorSubject<ClientRequestGroupTabIndex>(REQUESTS_TAB_INDEX);
  private _userHasInteractedWithTabs = false;

  selectRequestsTabOnGroupDeselection$ = this.isSelected$.pipe(
    pairwise(),
    filter(([wasSelected, isSelected]) => wasSelected && !isSelected),
    tap(_ => {
      this._userHasInteractedWithTabs = false;
      this.selectedTab$.next(REQUESTS_TAB_INDEX);
    }),
    takeUntil(this._unsubscribe$),
  );

  selectDeliverablesTabOnFocus$ = combineLatest([
    this.store.select(ClientRequestsState.getSlices.selectedGroupIdentity).pipe(
      ignoreNullish(), // if none present in URL, will stay open until manual group selection or navigation change because of this
      map(selectedGroupIdentity => selectedGroupIdentity === this.instance.identity),
    ),
    defer(() => this.instance.deliverables$).pipe(
      switchMap(deliverables => isEmpty(deliverables) ? of([]) : combineLatest(deliverables.map(deliverable => deliverable.data$))),
      map(deliverables => deliverables.map(({ id }) => id)),
    ),
    this.store.select(ClientRequestsState.getSlices.focusedDeliverableId).pipe(
      // stop emissions where focused deliverable ID is cleared (preserve last value)
      distinctUntilChanged((previous, current) => !current && !!previous),
    ),
  ]).pipe(
    take(1),
    filter(([isSelected, , focusedDeliverableId]) => isSelected && !!focusedDeliverableId),
    // determine if the focused deliverable has been deleted
    switchMap(([, deliverableIds, focusedDeliverableId]) => {
      if (focusedDeliverableId === ALL_DELIVERABLES && !isEmpty(deliverableIds)) {
        return of(true);
      }

      if (typeof focusedDeliverableId === 'number' && deliverableIds.includes(focusedDeliverableId)) {
        return of(true);
      }

      return typeof focusedDeliverableId === 'number'
        ? this.clientRequestApiService
          .isDeliverableActive(focusedDeliverableId)
          .pipe(
            tap((isActive) => {
              // don't open dialog if deliverable is still active or doesn't exist
              if (isActive !== false) {
                return;
              }

              this.dialog.open<
                DeletedDeliverableDialogComponent,
                DeletedDeliverableDialogData,
                boolean
              >(DeletedDeliverableDialogComponent, {
                data: { deliverableId: focusedDeliverableId },
              });
            })
          )
        : of(true);
    }),
    tap(_ => this.selectedTab$.next(DELIVERABLES_TAB_INDEX)),
    takeUntil(this._unsubscribe$),
  );


  constructor(
    private readonly store: Store,
    private readonly layoutService: ClientRequestsLayoutService,
    private readonly clientRequestApiService: ClientRequestApiService,
    private readonly dialog: MatDialog,
  ) {
    this.selectRequestsTabOnGroupDeselection$.subscribe();
  }

  ngOnInit(): void {
    this.isLocked$.pipe(takeUntil(this._unsubscribe$)).subscribe(locked => this.isLockedHostClass = locked);
    this.selectDeliverablesTabOnFocus$.subscribe();
    this.subscribeToSafeSendDeliverableUpdates();
    this.autoSelectTab$.subscribe();
  }

  /**
   * Auto-selects the appropriate tab based on whether requests exist.
   * Stops auto-selecting once the user manually clicks a tab.
   * Resets when the group is deselected.
   */
  private readonly autoSelectTab$ = combineLatest([
    defer(() => this.instance.requests$),
    defer(() => this.instance.deliverables$),
  ]).pipe(
    filter(() => !this._userHasInteractedWithTabs),
    map(([requests, deliverables]): ClientRequestGroupTabIndex =>
      requests.length === 0 && deliverables.length > 0 ? DELIVERABLES_TAB_INDEX : REQUESTS_TAB_INDEX,
    ),
    tap(tabIndex => this.selectedTab$.next(tabIndex)),
    takeUntil(this._unsubscribe$),
  );

  /**
   * Subscribes to real-time status updates for SafeSend deliverables in this group.
   * Updates the state when SafeSend status changes are received.
   */
  private subscribeToSafeSendDeliverableUpdates(): void {
    defer(() => this.instance.deliverables$).pipe(
      switchMap(deliverables => {
        if (isEmpty(deliverables)) {
          return of([]);
        }
        return combineLatest(deliverables.map(d => d.data$));
      }),
      take(1),
      map((deliverables: DeliverableModel[]) =>
        deliverables.filter(d => isSafeSendDeliverable(d)).map(d => d.id)
      ),
      filter(safeSendDeliverableIds => safeSendDeliverableIds.length > 0),
      switchMap(safeSendDeliverableIds =>
        this.clientRequestApiService.subscribeToDeliverableStatusUpdates(safeSendDeliverableIds).pipe(
          skip(1), // Skip initial emission (existing data when subscription connects)
          filter(deliverables => deliverables?.length > 0),
          tap(updatedDeliverables => {
            this.store.dispatch(new UpdateDeliverablesFromSubscription(updatedDeliverables));
          }),
        )
      ),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }


  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  onSelectedTabChange(index: ClientRequestGroupTabIndex): void {
    this._userHasInteractedWithTabs = true;
    this.selectedTab$.next(index);
  }

  @HostListener('click', ['$event'])
  async onClick(event: MouseEvent): Promise<void> {
    event.stopPropagation();

    // Don't navigate when the group is locked
    if (await firstValueFrom(this.isLocked$)) {
      return;
    }

    // Check if the click target is within a mat-tab-group or mat-checkbox
    const target = event.target as HTMLElement;
    if (target.closest('mat-tab-group') || target.closest('mat-checkbox')) {
      return; // Don't select the group if clicking on tabs or checkbox
    }

    const selectedGroupIdentity = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedGroupIdentity);
    if (selectedGroupIdentity) {
      return;
    }

    // SetSelectedClientRequestId
    this.instance.requests$.pipe( tap(requests => {
      if ( requests.length === 1 ) {
        const request  = requests[0];
        const identity = request.identity.replace(REQUEST_IDENTITY_TYPE, '').replace('-', '');
        this.store.dispatch(new SetSelectedClientRequestId(+identity as unknown as number));
      } else if ( requests.length === 0 ) {
        return;
      } else {
        this.store.dispatch(new SetSelectedClientRequestGroupIdentity(this.instance.identity));
      }
    }) ).subscribe();

  }

  readonly LoadableStatus = LoadableStatus;
}
